import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationIdsToMark,
  markAllNotificationsRead,
  markNotificationsRead,
} from '../../api/notifications';
import { invalidateAfterNotificationRead } from '../../lib/query/invalidateMutations';
import { queryKeys } from '../../lib/query/queryKeys';

function patchNotificationsList(prev, idsToMark) {
  if (!prev || idsToMark.length === 0) return prev;
  const idSet = new Set(idsToMark);
  return prev.map((n) => (idSet.has(n.id) ? { ...n, read: true } : n));
}

function patchAllNotificationsRead(prev) {
  if (!prev) return prev;
  return prev.map((n) => (n.read ? n : { ...n, read: true }));
}

export function useMarkNotificationRead(userId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ notification, notifications }) =>
      markNotificationsRead({ userId, notification, notifications }),
    onMutate: async ({ notification, notifications, filter = 'all' }) => {
      if (!userId) return {};

      const idsToMark = getNotificationIdsToMark(notification, notifications);
      if (idsToMark.length === 0) return {};

      const unreadKey = queryKeys.notifications.unreadCount(userId);
      const listKey = queryKeys.notifications.list(userId, filter);

      await queryClient.cancelQueries({ queryKey: unreadKey });
      await queryClient.cancelQueries({ queryKey: listKey });

      const previousCount = queryClient.getQueryData(unreadKey);
      const previousList = queryClient.getQueryData(listKey);

      queryClient.setQueryData(listKey, (prev) => patchNotificationsList(prev, idsToMark));
      queryClient.setQueryData(unreadKey, (prev) =>
        Math.max(0, (typeof prev === 'number' ? prev : 0) - idsToMark.length),
      );

      return { previousCount, previousList, idsToMark, filter };
    },
    onError: (_err, _variables, context) => {
      if (!userId || !context) return;
      if (context.previousCount !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.unreadCount(userId),
          context.previousCount,
        );
      }
      if (context.previousList !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.list(userId, context.filter ?? 'all'),
          context.previousList,
        );
      }
    },
    onSettled: () => {
      if (!userId) return;
      invalidateAfterNotificationRead(queryClient, { userId });
    },
  });
}

export function useMarkAllNotificationsRead(userId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(userId),
    onMutate: async ({ filter = 'all' } = {}) => {
      if (!userId) return {};

      const unreadKey = queryKeys.notifications.unreadCount(userId);

      await queryClient.cancelQueries({ queryKey: unreadKey });
      await queryClient.cancelQueries({ queryKey: ['notifications', 'list', userId] });

      const previousCount = queryClient.getQueryData(unreadKey);
      const previousLists = queryClient
        .getQueriesData({ queryKey: ['notifications', 'list', userId] })
        .map(([key, data]) => [key, data]);

      queryClient.setQueryData(unreadKey, 0);
      queryClient.setQueriesData({ queryKey: ['notifications', 'list', userId] }, (prev) =>
        patchAllNotificationsRead(prev),
      );

      return { previousCount, previousLists, filter };
    },
    onError: (_err, _variables, context) => {
      if (!userId || !context) return;
      if (context.previousCount !== undefined) {
        queryClient.setQueryData(
          queryKeys.notifications.unreadCount(userId),
          context.previousCount,
        );
      }
      context.previousLists?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      if (!userId) return;
      invalidateAfterNotificationRead(queryClient, { userId });
    },
  });
}
